/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export type GetAudioContextOptions = AudioContextOptions & {
  id?: string;
};

const map: Map<string, AudioContext> = new Map();

const resumeAudioContext = async (context: AudioContext): Promise<void> => {
  if (context.state !== 'suspended') return;
  await context.resume();
};

const activateAudioContext = async (
  context: AudioContext,
  id: string | undefined,
): Promise<AudioContext> => {
  try {
    await resumeAudioContext(context);
    return context;
  } catch (activationError: unknown) {
    if (id && map.get(id) === context) map.delete(id);
    if (context.state !== 'closed') {
      try {
        await context.close();
      } catch (_closeError: unknown) {}
    }
    throw activationError;
  }
};

export const audioContext: (
  options?: GetAudioContextOptions,
) => Promise<AudioContext> = async (options?: GetAudioContextOptions) => {
  if (options?.id && map.has(options.id)) {
    const existingContext = map.get(options.id);
    if (existingContext?.state === 'closed') {
      map.delete(options.id);
    } else if (existingContext) {
      return activateAudioContext(existingContext, options.id);
    }
  }
  const context = new AudioContext(options);
  if (options?.id) map.set(options.id, context);
  return activateAudioContext(context, options?.id);
};

export const blobToJSON = (blob: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        const json = JSON.parse(reader.result as string);
        resolve(json);
      } else {
        reject('oops');
      }
    };
    reader.readAsText(blob);
  });

export function base64ToArrayBuffer(base64: string) {
  var binaryString = atob(base64);
  var bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}
